import React, { useState, useEffect } from "react";
import { Input, Button, Switch, Drawer, Form, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { articleAPIWithErrorHandling, articleHelpers } from "../../../api/article/article";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["description", "status"];

export default function CategoryTable() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchCategories = async (page = 1, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search.trim(),
      };
      const response = await articleAPIWithErrorHandling.getCategories(params);
      const { articles: categoryData, pagination: paginationData } = articleHelpers.extractArticleList(response);

      setCategories(categoryData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách danh mục");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchCategories(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCategories(1, pagination.pageSize, value);
  };

  const handleReset = () => {
    setSearchText("");
    fetchCategories(1, pagination.pageSize, "");
  };

  const handleToggleStatus = async (id) => {
    try {
      await articleAPIWithErrorHandling.toggleCategoryStatus(id);
      message.success("Cập nhật trạng thái thành công");
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    if (await confirmDelete({ entity: "danh mục", name: record.name })) {
      try {
        await articleAPIWithErrorHandling.deleteCategory(record.id);
        message.success("Xóa danh mục thành công");
        fetchCategories(pagination.current, pagination.pageSize, searchText);
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  const showModal = (category = null) => {
    setEditingCategory(category);
    if (category) {
      form.setFieldsValue(category);
    } else {
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingCategory) {
        await articleAPIWithErrorHandling.updateCategory(editingCategory.id, values);
        message.success("Cập nhật danh mục thành công");
      } else {
        await articleAPIWithErrorHandling.createCategory(values);
        message.success("Tạo danh mục thành công");
      }

      setIsModalOpen(false);
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Đã có lỗi xảy ra");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "name", label: "Tên danh mục", fixed: true,
      column: {
        title: "Tên danh mục", dataIndex: "name", key: "name",
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
    },
    {
      key: "description", label: "Mô tả",
      column: {
        title: "Mô tả", dataIndex: "description", key: "description", ellipsis: true,
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 120,
        render: (status, record) => (
          <Switch
            checked={!!status}
            onChange={() => handleToggleStatus(record.id)}
            checkedChildren="Bật"
            unCheckedChildren="Tắt"
          />
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm danh mục..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!searchText;

  return (
    <BrandedPageLayout
      title="Danh mục bài viết"
      subtitle="Quản lý danh mục phân loại bài viết, tin tức."
      breadcrumbItems={[{ title: "Danh mục bài viết" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchCategories(pagination.current, pagination.pageSize, searchText)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm danh mục
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={categories}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={editingCategory ? "Cập nhật danh mục" : "Thêm danh mục mới"}
        placement="right"
        width="min(520px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleModalOk}>
              {editingCategory ? "Cập nhật" : "Thêm mới"}
            </Button>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: true }}
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: "Vui lòng nhập tên danh mục!" }]}
          >
            <Input placeholder="Ví dụ: Đời sống, Pháp luật..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea placeholder="Nhập mô tả cho danh mục..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Trạng thái"
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>
        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
